import { useEffect, useState, useRef } from "react";
import { doc, updateDoc, deleteDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  getAllSKUs, addSKU, deleteSKU, getSKUFieldConfig, saveSKUFieldConfig,
  getSkuMaster, applySkuMaster, remapSkusFromSpecs, skuFromMaster,
} from "../../firebase/services";
import { parseMasterFile } from "../../utils/masterSpecs";
import SkuSpecSheet, { resetMasterColumnsCache } from "../../components/SkuSpecSheet";
import { useLanguage } from "../../hooks/useLanguage";

const LS_KEY = "promoterhq_sku_fields_v3";

// ── Danh sách cột dự phòng (khi chưa up master nào); có master thì dùng cột của file master ──
export const SHEET_COLUMNS = [
  "Part No","Platform","Series Name","Segment Name","Model","MKT Spec","Color_Website",
  "A Side","B Side","C Side","D Side","Screen Size","Resolution","LCD Features","Screen Touch",
  "Frame Rate","Response Time","Color Range","Screen Size_Website","Pixel_Website",
  "Frame Rate_Website","Color Range_Website","Display Type_Website","Peak Brightness (HDR Value)",
  "Touch Feature_Website","Typical Brightness","Eye Care Feature","CPU Core Name","CPU Number",
  "CPU Basic Clock","NPU TOPS","CPU Number_Website","CPU Specification_Website","Chipset SoC_Website",
  "GPU1 Vendor","GPU1 Number","GPU1 MKT Name_Website","GPU1 Boost Clock_Website","GPU1 Remark_Website",
  "Combined Power_Website","OverBoost Ultra Remark_Website","Scenario Remark_Website","Thermal","Fan",
  "Fan Technology","Pipe","Vapor Chamber","GPU1 VRAM Size","GPU1 VRAM Type","GPU1 Graphics_Website",
  "Memory Size","Memory Module","Memory(Slot) Max Speed","Memory Form","Memory Remark_Website",
  "Memory Max Speed","Memory Slot","Memory Max Capacity_Website","SSD Form Factor","SSD Size",
  "SSD Interface","SSD 2 Size","SSD 2 Interface","HDD size","2.5 HDD_Website","SSD Remark",
  "SSD (NVMe PCIe Gen)_Website","HDD_Website","Max Storage Capacity","LAN_Website","LAN","RJ45",
  "WLAN/BT_Website","WLAN Version","WLAN/BT",
  "Type-C (USB3.1 Gen2 / DP / Thunderbolt™3)","Type-C (USB3.1 Gen2 / DP / Thunderbolt™3 optional)",
  "Type-C (USB3.2 Gen2 / DP / Thunderbolt™3)","Type-C (USB3.2 Gen1 / DP / Thunderbolt™3)",
  "Type-C (USB3.2 Gen2 / DP / Thunderbolt™3) with PD charging","Type-C (USB3.2 Gen2 / DisplayPort™)",
  "Thunderbolt™ 4 (DisplayPort™/ Power Delivery 3.0)","Thunderbolt™ 4 (DisplayPort™/ Power Delivery 3.1)",
  "Thunderbolt™ 4 (DisplayPort™)","Thunderbolt™ 5 (DisplayPort™/ Power Delivery 3.1)",
  "Thunderbolt™ (DisplayPort™/ Power Delivery 3.0)",
  "Type-C (USB4® / DisplayPort™/ Power Delivery 3.0/ Thunderbolt™ 4 compatible)",
  "Type-C (USB4® / DisplayPort™/ Power Delivery 3.1/ Thunderbolt™ 4 compatible)",
  "Type-C (USB3.2 Gen2 / DisplayPort™/ Power Delivery 3.0)",
  "Type-C (USB3.2 Gen2 / DisplayPort™/ Power Delivery 3.1)",
  "Type-C (USB3.2 Gen1 / DisplayPort™/ Power Delivery 3.0)",
  "Type-C (USB3.1 Gen1 / DisplayPort™)","Type-C (USB3.2 Gen1 / DisplayPort™)",
  "Type-C USB3.1 Gen2","Type-C USB3.2 Gen2","Type-C USB3.2 Gen2 with PD charging",
  "Type-C USB3.1 Gen1","Type-C USB3.2 Gen1","Type-A USB3.1 Gen1","Type-A USB3.2 Gen1",
  "Type-A USB3.1 Gen2","Type-A USB3.2 Gen2","Type-C USB3.2 Gen2x2","Type-A USB2.0",
  "USB3.2 Gen 2 Type-C(connect to computer)","CardReader","Smart Card Reader",
  "HDMI™","DisplayPort","Mini Display Port","Keyboard Types_Website","Keyboard Feature",
  "Keyboard Keys","Key Travel","Keyboard Numeric","Keyboard Color","Keyboard Language",
  "Touchpad","WebCam","Audio Jack_Website","Audio Speaker","Audio Woofer","Audio Mic",
  "Audio Solution_Website","Adaptor","Adaptor Unit Weight (g)","Power Cord Unit Weight (g)",
  "Power Cord Type","S0/S0ix PD charging range","S4/S5 PD charging range",
  "Battery Cell_Website","Whrs","Mah","Battery Unit Weight (g)","Support Fast Charging",
  "OS_Website","Copilot+ PC Certified","Dimension","Dimension(Inches)_Website","Weight",
  "Weight(lbs)_Website","Security Type_Website","Certification Type_Website","Energy Star 9.0",
  "Fingerprint","Ambient Light Sensor_Website","Gyroscope_Website","Accelerometer_Website",
  "Magnetometer_Website","Proximity Sensor","Adaptive Color Sensor","NFC","Warranty_Website",
  "Extra Bag","Extra Mouse","Software","EAN",
];

export const DEFAULT_ALL_FIELDS = [
  { key:"partNo",   label:"Part No",    type:"single", columns:["Part No"],                                                                              protected:true,  visible:true  },
  { key:"model",    label:"Model",      type:"single", columns:["MKT Spec"],                                                                             protected:true,  visible:true  },
  { key:"series",   label:"Series",     type:"single", columns:["Series Name"],                                                                          protected:false, visible:true  },
  { key:"segment",  label:"Segment",    type:"single", columns:["Segment Name"],                                                                         protected:false, visible:true  },
  { key:"cpu",      label:"CPU",        type:"concat", columns:["CPU Number","CPU Basic Clock"],                                                         protected:false, visible:true  },
  { key:"gpu",      label:"GPU",        type:"single", columns:["GPU1 MKT Name_Website"],                                                               protected:false, visible:true  },
  { key:"ram",      label:"RAM",        type:"concat", columns:["Memory Size","Memory Module"],                                                         protected:false, visible:true  },
  { key:"ssd",      label:"SSD",        type:"concat", columns:["SSD Size","SSD Interface"],                                                            protected:false, visible:true  },
  { key:"lcd",      label:"Màn hình",   type:"concat", columns:["Screen Size_Website","Pixel_Website","Frame Rate_Website","Color Range_Website"],       protected:false, visible:false },
  { key:"memSlots", label:"Khe RAM",    type:"single", columns:["Memory Slot"],                                                                         protected:false, visible:true  },
  { key:"ssdSlots", label:"Khe SSD",    type:"single", columns:["SSD (NVMe PCIe Gen)_Website"],                                                        protected:false, visible:true  },
];

// localStorage cache
function cacheFields(f)  { localStorage.setItem(LS_KEY, JSON.stringify(f)); }
function cachedFields()  { try { const s = JSON.parse(localStorage.getItem(LS_KEY)); return s?.length ? s : null; } catch { return null; } }

function useDragReorder(items, setItems) {
  const dragIdx = useRef(null);
  const onDragStart = i => { dragIdx.current = i; };
  const onDragOver = (e, i) => {
    e.preventDefault();
    if (dragIdx.current === null || dragIdx.current === i) return;
    const next = [...items];
    const [moved] = next.splice(dragIdx.current, 1);
    next.splice(i, 0, moved);
    dragIdx.current = i;
    setItems(next);
  };
  const onDragEnd = () => { dragIdx.current = null; };
  return { onDragStart, onDragOver, onDragEnd };
}

function uniqueVals(skus, key) {
  const vals = new Set();
  skus.forEach(s => { if (s[key]) vals.add(s[key]); });
  return ["", ...Array.from(vals).sort()];
}

// ── Searchable column picker dropdown ──
function ColPicker({ value, onChange, sheetCols, placeholder = "Chọn cột…" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ]       = useState("");
  const ref             = useRef(null);
  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const filtered = sheetCols.filter(c => c.toLowerCase().includes(q.toLowerCase()));
  const isValid  = value && sheetCols.includes(value);
  return (
    <div ref={ref} style={{ position:"relative", display:"inline-block" }}>
      <button type="button" onClick={() => { setOpen(o => !o); setQ(""); }}
        style={{ background:"#111", border:`1px solid ${isValid?"#166534":value?"#7f1d1d":"#222"}`,
          borderRadius:4, padding:"3px 8px", color:isValid?"#22c55e":value?"#ef4444":"#555",
          fontSize:11, fontFamily:"monospace", cursor:"pointer", maxWidth:220,
          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}
        title={value || placeholder}>
        {value || <span style={{ color:"#444" }}>{placeholder}</span>}
        <span style={{ marginLeft:4, color:"#333" }}>▾</span>
      </button>
      {open && (
        <div style={{ position:"absolute", top:"calc(100% + 4px)", left:0, zIndex:9999,
          background:"#0d0d1a", border:"1px solid #1e1e2e", borderRadius:6,
          width:300, boxShadow:"0 8px 24px rgba(0,0,0,.7)" }}>
          <div style={{ padding:"6px 8px", borderBottom:"1px solid #1a1a1a" }}>
            <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Tìm cột…"
              style={{ width:"100%", background:"#111", border:"1px solid #222", borderRadius:3,
                padding:"4px 7px", color:"#fff", fontSize:11, fontFamily:"monospace" }} />
          </div>
          <div style={{ maxHeight:200, overflowY:"auto" }}>
            {value && <div onClick={() => { onChange(""); setOpen(false); }}
              style={{ padding:"5px 10px", fontSize:11, color:"#ef4444", cursor:"pointer", borderBottom:"1px solid #111" }}>
              ✕ Xóa
            </div>}
            {filtered.length === 0 && <div style={{ padding:"8px 10px", fontSize:11, color:"#444" }}>Không tìm thấy</div>}
            {filtered.map(col => (
              <div key={col} onClick={() => { onChange(col); setOpen(false); setQ(""); }}
                style={{ padding:"5px 10px", fontSize:11, fontFamily:"monospace", cursor:"pointer",
                  color:col===value?"#22c55e":"#aaa", background:col===value?"#052e16":"transparent" }}
                onMouseEnter={e => { if (col!==value) e.currentTarget.style.background="#111"; }}
                onMouseLeave={e => { if (col!==value) e.currentTarget.style.background="transparent"; }}>
                {col}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const PAGE_SIZES = [10, 50, 100];
const TH_BASE = { fontSize:12, fontWeight:600, background:"#C8152A", borderBottom:"2px solid #A01020", textAlign:"left", color:"#fff", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", userSelect:"none" };
const TC_BASE = { fontSize:12, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" };
function colWidth(key) { return ({model:185,cpu:115,gpu:105,partNo:85,series:75,segment:70})[key]??80; }

// Auto filter type: ≤20 distinct → dropdown, else → text
function filterType(skus, key) {
  const vals = new Set(skus.map(s => s[key]).filter(Boolean));
  return vals.size <= 20 ? "select" : "text";
}

export default function AdminSKUs() {
  const { t } = useLanguage();
  const [skus, setSKUs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm]         = useState({});
  const [checkedIds, setCheckedIds] = useState(new Set());

  const [page, setPage]         = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters]   = useState({});
  const setFilter = (k, v) => { setFilters(f => ({ ...f, [k]: v })); setPage(0); };
  const clearFilters = () => { setFilters({}); setPage(0); };

  const [syncing, setSyncing]         = useState(false);
  const [syncPreview, setSyncPreview] = useState(null);
  const [syncMsg, setSyncMsg]         = useState("");
  const [liveColumns, setLiveColumns] = useState(SHEET_COLUMNS);
  const [master, setMaster]           = useState(null);       // skuMaster/current
  const [statusFilter, setStatusFilter] = useState("active"); // active | discontinued | all
  const fileRef = useRef(null);

  const [showFieldConfig, setShowFieldConfig] = useState(false);
  const [fields, setFields]   = useState(cachedFields() || DEFAULT_ALL_FIELDS);
  const [saving, setSaving]   = useState(false);
  const [saveMsg, setSaveMsg] = useState("");
  const [newField, setNewField] = useState({ key:"", label:"", type:"single" });

  // draft = working copy; only persisted to Firestore on explicit save
  const [draft, setDraft] = useState(null); // null = not editing

  // On mount: load from Firestore, override local cache
  useEffect(() => {
    getSKUFieldConfig().then(remote => {
      if (remote?.length) { setFields(remote); cacheFields(remote); }
    });
    getAllSKUs().then(setSKUs);
    getSkuMaster().then(m => { setMaster(m); if (m?.columns?.length) setLiveColumns(m.columns); }).catch(() => {});
  }, []);

  useEffect(() => { setPage(0); }, [pageSize]);

  const workingFields = draft ?? fields;
  const visibleFields = workingFields.filter(f => f.visible);

  // ── Open config = start a draft ──
  const openConfig = () => {
    setDraft(fields.map(f => ({ ...f, columns: [...f.columns] })));
    setShowFieldConfig(true);
  };
  const cancelConfig = () => { setDraft(null); setShowFieldConfig(false); setSaveMsg(""); };

  const updateDraft = (key, patch) =>
    setDraft(d => d.map(f => f.key === key ? { ...f, ...patch } : f));
  const removeDraftField = key =>
    setDraft(d => d.filter(f => f.key !== key));
  const addColToDraft = (key, col) => {
    if (!col) return;
    setDraft(d => d.map(f => f.key === key
      ? { ...f, columns: f.columns.includes(col) ? f.columns : [...f.columns, col] }
      : f));
  };
  const removeColFromDraft = (key, ci) =>
    setDraft(d => d.map(f => f.key === key
      ? { ...f, columns: f.columns.filter((_, i) => i !== ci) }
      : f));
  const moveColUp = (key, ci) =>
    setDraft(d => d.map(f => {
      if (f.key !== key || ci === 0) return f;
      const cols = [...f.columns];
      [cols[ci-1], cols[ci]] = [cols[ci], cols[ci-1]];
      return { ...f, columns: cols };
    }));

  const drag = useDragReorder(draft || [], setDraft);

  const addNewField = () => {
    if (!newField.key || !newField.label) return;
    if ((draft||[]).find(f => f.key === newField.key)) { alert("Key đã tồn tại."); return; }
    setDraft(d => [...(d||[]), { key:newField.key, label:newField.label, type:newField.type, columns:[], protected:false, visible:true }]);
    setNewField({ key:"", label:"", type:"single" });
  };
  const resetDraft = () => { if (window.confirm("Reset về mặc định?")) setDraft(DEFAULT_ALL_FIELDS.map(f=>({...f,columns:[...f.columns]}))); };

  // ── Save & Sync to Firestore ──
  const handleSaveSync = async () => {
    if (!draft) return;
    setSaving(true); setSaveMsg("");
    try {
      await saveSKUFieldConfig(draft);
      setFields(draft); cacheFields(draft);
      const savedFields = draft;
      setDraft(null);
      setShowFieldConfig(false);
      let remapped = 0;
      if (master && window.confirm(t("skm.remap_confirm"))) {
        remapped = await remapSkusFromSpecs(savedFields);
        getAllSKUs().then(setSKUs);
      }
      setSaveMsg("✅ Đã lưu & đồng bộ sang supervisor/promoter." + (remapped ? " " + t("skm.remapped", { n: remapped }) : ""));
      setTimeout(() => setSaveMsg(""), 4000);
    } catch (err) { setSaveMsg("❌ Lỗi: " + err.message); }
    setSaving(false);
  };

  // ── Filtered / paged ──
  const isDisc = (s) => s.status === "discontinued";
  const discCount = skus.filter(isDisc).length;
  const filteredSkus = skus.filter(s => {
    if (statusFilter === "active" && isDisc(s)) return false;
    if (statusFilter === "discontinued" && !isDisc(s)) return false;
    for (const [key, val] of Object.entries(filters)) {
      if (!val) continue;
      if (key === "loai") {
        if (val === "highend" && !s.isHighend) return false;
        if (val === "standard" && s.isHighend) return false;
      } else if (filterType(skus, key) === "select") {
        if (s[key] !== val) return false;
      } else {
        if (!s[key]?.toLowerCase().includes(val.toLowerCase())) return false;
      }
    }
    return true;
  });
  const totalPages    = Math.ceil(filteredSkus.length / pageSize);
  const pagedSkus     = filteredSkus.slice(page * pageSize, (page+1)*pageSize);
  const activeFilters = Object.values(filters).filter(Boolean).length;

  // ── Up file master (.xlsx, sheet NB) ──
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setSyncing(true); setSyncPreview(null); setSyncMsg(t("skm.reading", { name: file.name }));
    try {
      const parsed = await parseMasterFile(await file.arrayBuffer());
      const products = parsed.products.filter(p => /^9S7-/i.test(p.partNo));
      const skipped = parsed.products.length - products.length;
      if (!products.length) throw new Error(t("skm.no_products"));
      const existingMap = {};
      skus.forEach(x => { if (x.partNo) existingMap[x.partNo] = x; });
      const keys = fields.map(f => f.key);
      const inMaster = new Set(products.map(p => p.partNo));
      const rows = products.map(p => {
        const mapped = skuFromMaster(p, fields);
        const ex = existingMap[p.partNo];
        const state = !ex ? "new" : isDisc(ex) ? "back" : keys.some(k => (ex[k] || "") !== (mapped[k] || "")) ? "changed" : "same";
        return { mapped, state };
      });
      const count = (st) => rows.filter(r => r.state === st).length;
      const leaving = skus.filter(x => x.partNo && !inMaster.has(x.partNo) && !isDisc(x));
      setLiveColumns(parsed.columns);
      setSyncPreview({ file, parsed, products, rows, leaving, skipped,
        newCount: count("new"), updCount: count("changed"), backCount: count("back"), sameCount: count("same") });
      setSyncMsg(t("skm.found", { n: products.length, sheet: parsed.sheetName, cols: parsed.columns.length }));
    } catch (err) { setSyncMsg("❌ Lỗi: " + err.message); }
    setSyncing(false);
  };

  const handleConfirmSync = async () => {
    if (!syncPreview) return;
    setSyncing(true); setSyncMsg(t("skm.applying"));
    try {
      const r = await applySkuMaster({
        fileName: syncPreview.file.name, master: syncPreview.parsed, products: syncPreview.products,
        fields, existingSkus: skus, byName: "Admin",
      });
      setSyncMsg("✅ " + t("skm.done", { added: r.added, updated: r.updated, disc: r.discontinued }));
      setSyncPreview(null);
      resetMasterColumnsCache();
      getSkuMaster().then(setMaster).catch(() => {});
      getAllSKUs().then(setSKUs);
    } catch (err) { setSyncMsg("❌ Lỗi: " + err.message); }
    setSyncing(false);
  };

  // ── Bulk ──
  const pageIds        = pagedSkus.map(s => s.id);
  const allPageChecked = pageIds.length>0 && pageIds.every(id => checkedIds.has(id));
  const togglePage = () => {
    const next = new Set(checkedIds);
    if (allPageChecked) pageIds.forEach(id=>next.delete(id)); else pageIds.forEach(id=>next.add(id));
    setCheckedIds(next);
  };
  const toggleOne = id => { const n=new Set(checkedIds); n.has(id)?n.delete(id):n.add(id); setCheckedIds(n); };
  const handleBulkDelete = async () => {
    if (!checkedIds.size||!window.confirm(`Xóa ${checkedIds.size} SKU?`)) return;
    for (const id of checkedIds) await deleteDoc(doc(db,"skus",id)).catch(()=>{});
    setCheckedIds(new Set()); getAllSKUs().then(setSKUs);
  };
  const handleBulkSetHighend = async isHighend => {
    for (const id of checkedIds) await updateDoc(doc(db,"skus",id),{isHighend}).catch(()=>{});
    setCheckedIds(new Set()); getAllSKUs().then(setSKUs);
  };

  // ── Add / Edit ──
  const handleAdd = async e => {
    e.preventDefault();
    await addSKU({ ...form, isHighend:!!form.isHighend });
    setShowForm(false); setForm({}); getAllSKUs().then(setSKUs);
  };
  const openEdit = s => { setSelected(s); setEditForm({ isHighend:s.isHighend, notes:s.notes||"" }); };
  const saveEdit = async () => {
    await updateDoc(doc(db,"skus",selected.id),{ isHighend:editForm.isHighend, notes:editForm.notes, updatedAt:serverTimestamp() });
    setSelected(null); setEditForm(null); getAllSKUs().then(setSKUs);
  };

  const FI = { width:"100%", background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, padding:"4px 6px", color:"#333", fontSize:11, fontFamily:"inherit", outline:"none" };
  const FS = { width:"100%", background:"#fff", border:"1px solid #e8e8e8", borderRadius:4, padding:"4px 4px", color:"#333", fontSize:11, fontFamily:"inherit", outline:"none" };

  // ── Sheet columns panel (right side of config) ──
  const [colSearch, setColSearch] = useState("");
  const filteredCols = liveColumns.filter(c => c.toLowerCase().includes(colSearch.toLowerCase()));

  return (
    <>
      <div className="page-header">
        <div>
          <h1>{t("sku.title")}</h1>
          <span style={{ fontSize:11, color:"#555" }}>
            {filteredSkus.length!==skus.length?`${filteredSkus.length} / ${skus.length}`:skus.length} sản phẩm
            {activeFilters>0 && <span style={{ color:"#f59e0b", marginLeft:6 }}>● {activeFilters} bộ lọc</span>}
          </span>
          <div style={{ fontSize:11, color:"#888", marginTop:2 }}>
            {master
              ? t("skm.current", { name: master.fileName, n: master.count,
                  d: master.uploadedAt?.toDate ? master.uploadedAt.toDate().toLocaleString("vi-VN") : "" })
              : t("skm.none")}
          </div>
        </div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(0); }} style={{ fontSize:12, padding:"5px 8px", width:"auto" }}>
            <option value="active">{t("skm.st_active")}</option>
            <option value="discontinued">{t("skm.st_disc")} ({discCount})</option>
            <option value="all">{t("skm.st_all")}</option>
          </select>
          {activeFilters>0 && <button className="btn btn-sm" onClick={clearFilters}>✕ Xóa bộ lọc</button>}
          <button className="btn" onClick={showFieldConfig ? cancelConfig : openConfig}>
            {showFieldConfig ? "✕ Đóng cấu hình" : "⚙ Cấu hình trường"}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx" style={{ display:"none" }} onChange={handleFile} />
          <button className="btn btn-primary" onClick={() => fileRef.current?.click()} disabled={syncing}>
            {syncing ? "⏳ " + t("skm.busy") : "📤 " + t("skm.upload")}
          </button>
          <button className="btn" onClick={() => setShowForm(v=>!v)}>+ {t("sku.add")}</button>
        </div>
      </div>

      {saveMsg && (
        <div className={`alert ${saveMsg.startsWith("✅")?"alert-success":"alert-error"}`} style={{ marginBottom:12 }}>
          {saveMsg}
        </div>
      )}

      {/* ══════════════════ FIELD CONFIG PANEL ══════════════════ */}
      {showFieldConfig && draft && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header">
            <div className="card-title">⚙ Cấu hình trường sản phẩm</div>
            <div style={{ display:"flex", gap:6, alignItems:"center" }}>
              <button className="btn btn-sm" onClick={resetDraft}>↩ Reset</button>
              <button className="btn btn-primary btn-sm" onClick={handleSaveSync} disabled={saving}>
                {saving ? "⏳ Đang lưu..." : "💾 Lưu & Đồng bộ"}
              </button>
              <button className="btn btn-sm" onClick={cancelConfig}>✕ Hủy</button>
            </div>
          </div>
          <p style={{ fontSize:11, color:"#666", marginBottom:12 }}>
            Kéo <b>⠿</b> đổi thứ tự. <b>Single</b>: lấy cột đầu tiên, fallback xuống cột tiếp theo.{" "}
            <b>Concat</b>: ghép tất cả cột. Bấm <b>💾 Lưu &amp; Đồng bộ</b> để đẩy config sang supervisor/promoter.
          </p>

          {/* Split layout: left = field config, right = sheet columns */}
          <div style={{ display:"flex", gap:16, alignItems:"flex-start" }}>

            {/* ── LEFT: field mapping table ── */}
            <div style={{ flex:1, minWidth:0, overflowX:"auto" }}>
              <table style={{ width:"100%", fontSize:12, borderCollapse:"collapse" }}>
                <thead>
                  <tr>
                    {["","Key","Label","Type","Cột Sheet","Hiện",""].map((h,i) => (
                      <th key={i} style={{ textAlign:"left", padding:"6px 8px", color:"#555",
                        borderBottom:"1px solid #1a1a1a", fontSize:11, whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {draft.map((f, i) => (
                    <tr key={f.key} draggable
                      onDragStart={() => drag.onDragStart(i)}
                      onDragOver={e => drag.onDragOver(e, i)}
                      onDragEnd={drag.onDragEnd}
                      style={{ borderBottom:"1px solid #0f0f0f", verticalAlign:"top" }}>
                      <td style={{ padding:"8px 6px", color:"#333", fontSize:16, cursor:"grab", userSelect:"none" }}>⠿</td>
                      <td style={{ padding:"8px 8px" }}>
                        <code style={{ color:f.protected?"#f59e0b":"#7dd3fc", fontSize:11 }}>
                          {f.key}{f.protected&&" 🔒"}
                        </code>
                      </td>
                      <td style={{ padding:"6px 6px" }}>
                        <input value={f.label} onChange={e => updateDraft(f.key,{label:e.target.value})}
                          style={{ width:96, background:"#111", border:"1px solid #222", borderRadius:3,
                            padding:"3px 6px", color:"#fff", fontSize:11 }} />
                      </td>
                      <td style={{ padding:"6px 6px" }}>
                        <select value={f.type} onChange={e => updateDraft(f.key,{type:e.target.value})}
                          style={{ background:"#111", border:"1px solid #222", borderRadius:3,
                            padding:"3px 4px", color:"#aaa", fontSize:11, width:70 }}>
                          <option value="single">Single</option>
                          <option value="concat">Concat</option>
                        </select>
                      </td>
                      <td style={{ padding:"6px 6px", minWidth:240 }}>
                        <div style={{ display:"flex", flexDirection:"column", gap:4 }}>
                          {f.columns.map((col, ci) => {
                            const exists = liveColumns.some(c=>c.toLowerCase()===col.toLowerCase());
                            return (
                              <div key={ci} style={{ display:"flex", alignItems:"center", gap:3 }}>
                                {f.type==="single" && f.columns.length>1 && (
                                  <span style={{ fontSize:9, color:"#444", width:12, textAlign:"right", flexShrink:0 }}>{ci+1}.</span>
                                )}
                                <span style={{
                                  background:exists?"#052e16":"#1c0505",
                                  border:`1px solid ${exists?"#166534":"#7f1d1d"}`,
                                  borderRadius:4, padding:"2px 7px", fontSize:11,
                                  fontFamily:"monospace", color:exists?"#22c55e":"#ef4444",
                                  flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                                }} title={col}>{exists?"✓":"✗"} {col}</span>
                                {ci>0 && (
                                  <button type="button" onClick={() => moveColUp(f.key, ci)}
                                    style={{ background:"none", border:"1px solid #222", borderRadius:3,
                                      padding:"1px 5px", color:"#444", cursor:"pointer", fontSize:11 }}
                                    title="Ưu tiên cao hơn">↑</button>
                                )}
                                <button type="button" onClick={() => removeColFromDraft(f.key, ci)}
                                  style={{ background:"none", border:"1px solid #333", borderRadius:3,
                                    padding:"1px 5px", color:"#555", cursor:"pointer", fontSize:11 }}>×</button>
                              </div>
                            );
                          })}
                          <ColPicker value="" sheetCols={liveColumns.filter(c=>!f.columns.some(x=>x.toLowerCase()===c.toLowerCase()))}
                            placeholder={f.columns.length===0?"Chọn cột…":"+ Thêm cột"}
                            onChange={col => addColToDraft(f.key, col)} />
                        </div>
                      </td>
                      <td style={{ padding:"8px 8px", textAlign:"center" }}>
                        <input type="checkbox" checked={!!f.visible}
                          onChange={e => updateDraft(f.key,{visible:e.target.checked})}
                          style={{ width:"auto", cursor:"pointer" }} />
                      </td>
                      <td style={{ padding:"6px 8px", textAlign:"right" }}>
                        {!f.protected
                          ? <button className="btn btn-sm btn-danger" onClick={() => removeDraftField(f.key)}>Xóa</button>
                          : <span style={{ fontSize:10, color:"#222" }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Add new field */}
              <div style={{ background:"#0a0a14", padding:"10px 12px", borderRadius:6, marginTop:10,
                borderTop:"1px solid #111", display:"flex", gap:8, alignItems:"flex-end", flexWrap:"wrap" }}>
                <span style={{ fontSize:11, color:"#555", alignSelf:"center" }}>+ Trường mới:</span>
                <div>
                  <div style={{ fontSize:10, color:"#555", marginBottom:3 }}>Key</div>
                  <input value={newField.key} onChange={e=>setNewField(f=>({...f,key:e.target.value.replace(/\s/g,"")}))}
                    placeholder="camelCase" style={{ background:"#111", border:"1px solid #222", borderRadius:3,
                      padding:"4px 7px", color:"#fff", fontSize:11, fontFamily:"monospace", width:110 }} />
                </div>
                <div>
                  <div style={{ fontSize:10, color:"#555", marginBottom:3 }}>Label</div>
                  <input value={newField.label} onChange={e=>setNewField(f=>({...f,label:e.target.value}))}
                    placeholder="Tên hiển thị" style={{ background:"#111", border:"1px solid #222", borderRadius:3,
                      padding:"4px 7px", color:"#fff", fontSize:11, width:120 }} />
                </div>
                <div>
                  <div style={{ fontSize:10, color:"#555", marginBottom:3 }}>Type</div>
                  <select value={newField.type} onChange={e=>setNewField(f=>({...f,type:e.target.value}))}
                    style={{ background:"#111", border:"1px solid #222", borderRadius:3, padding:"4px 5px", color:"#aaa", fontSize:11, width:80 }}>
                    <option value="single">Single</option>
                    <option value="concat">Concat</option>
                  </select>
                </div>
                <button className="btn btn-primary btn-sm" onClick={addNewField} disabled={!newField.key||!newField.label}>
                  + Thêm (chọn cột sau)
                </button>
              </div>
            </div>

            {/* ── RIGHT: Sheet columns reference panel ── */}
            <div style={{ width:240, flexShrink:0, background:"#080810", border:"1px solid #1a1a1a",
              borderRadius:6, overflow:"hidden" }}>
              <div style={{ padding:"8px 10px", borderBottom:"1px solid #1a1a1a",
                fontSize:11, color:"#555", fontWeight:500 }}>
                📋 {t("skm.master_cols")} ({liveColumns.length})
              </div>
              <div style={{ padding:"6px 8px", borderBottom:"1px solid #111" }}>
                <input value={colSearch} onChange={e=>setColSearch(e.target.value)}
                  placeholder="Tìm cột…"
                  style={{ width:"100%", background:"#111", border:"1px solid #1a1a1a", borderRadius:3,
                    padding:"3px 7px", color:"#aaa", fontSize:11, fontFamily:"monospace" }} />
              </div>
              <div style={{ maxHeight:460, overflowY:"auto" }}>
                {filteredCols.map((col, i) => {
                  const usedBy = (draft||[]).find(f => f.columns.some(c=>c.toLowerCase()===col.toLowerCase()));
                  return (
                    <div key={i}
                      title={usedBy ? `Đang dùng cho: ${usedBy.label}` : col}
                      style={{ padding:"4px 10px", fontSize:10, fontFamily:"monospace",
                        color: usedBy?"#22c55e":"#444",
                        borderBottom:"1px solid #0d0d0d",
                        display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <span style={{ overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>{col}</span>
                      {usedBy && <span style={{ fontSize:9, color:"#166534", marginLeft:4, flexShrink:0 }}>✓ {usedBy.label}</span>}
                    </div>
                  );
                })}
              </div>
            </div>

          </div>{/* end split */}
        </div>
      )}

      {/* ══════════════════ SYNC PANEL ══════════════════ */}
      {syncMsg && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header" style={{ justifyContent:"space-between" }}>
            <div className="card-title">📤 {t("skm.panel_title")}</div>
            {!syncing && <button className="btn btn-sm" onClick={()=>{setSyncMsg("");setSyncPreview(null);}}>✕</button>}
          </div>
          <div style={{ fontSize:12, marginBottom:8, color:syncMsg.startsWith("✅")?"#22c55e":syncMsg.startsWith("❌")?"#ef4444":"#aaa" }}>{syncMsg}</div>
          {syncPreview && (
            <>
              <div style={{ display:"flex", gap:8, flexWrap:"wrap", marginBottom:10, fontSize:12 }}>
                <span className="badge badge-green">+ {syncPreview.newCount} {t("skm.k_new")}</span>
                <span className="badge badge-amber">↺ {syncPreview.updCount} {t("skm.k_changed")}</span>
                {syncPreview.backCount > 0 && <span className="badge badge-blue">⟲ {syncPreview.backCount} {t("skm.k_back")}</span>}
                <span className="badge badge-gray">— {syncPreview.sameCount} {t("skm.k_same")}</span>
                <span className="badge badge-red">⛔ {syncPreview.leaving.length} {t("skm.k_leaving")}</span>
              </div>
              {syncPreview.leaving.length > 0 && (
                <div className="alert alert-warn" style={{ fontSize:12, marginBottom:10 }}>
                  {t("skm.leaving_note")}{" "}
                  <span style={{ color:"#555" }}>{syncPreview.leaving.slice(0, 10).map(x => x.model).join(", ")}{syncPreview.leaving.length > 10 ? "…" : ""}</span>
                </div>
              )}
              {(syncPreview.skipped > 0 || syncPreview.parsed.warnings.length > 0) && (
                <div style={{ fontSize:11, color:"#888", marginBottom:8 }}>
                  {syncPreview.skipped > 0 && <div>{t("skm.skipped", { n: syncPreview.skipped })}</div>}
                  {syncPreview.parsed.warnings.slice(0, 5).map((w, i) => <div key={i}>⚠ {w}</div>)}
                </div>
              )}
              <div style={{ fontSize:11, color:"#888", marginBottom:8 }}>{t("skm.keep_note")}</div>
              <div style={{ overflowX:"auto", marginBottom:10 }}>
                <table style={{ fontSize:11, borderCollapse:"collapse", tableLayout:"fixed", minWidth:600 }}>
                  <thead><tr>
                    <th style={{ ...TH_BASE, padding:"4px 6px", width:28 }}>St</th>
                    {visibleFields.map(f=><th key={f.key} style={{ ...TH_BASE, padding:"4px 6px" }}>{f.label}</th>)}
                    <th style={{ ...TH_BASE, padding:"4px 6px", width:28 }}>Hi</th>
                  </tr></thead>
                  <tbody>
                    {[...syncPreview.rows].sort((a, b) => (a.state === "same") - (b.state === "same")).slice(0,50).map((row,i)=>{
                      const r = row.mapped;
                      const ex = skus.find(x => x.partNo === r.partNo);
                      return (
                        <tr key={i} style={{ borderBottom:"1px solid #0f0f0f", opacity:row.state==="same"?0.35:1 }}>
                          <td style={{ padding:"3px 6px" }}>{row.state==="new"?<span style={{color:"#22c55e"}}>+</span>:row.state==="changed"?<span style={{color:"#f59e0b"}}>↺</span>:row.state==="back"?<span style={{color:"#2563eb"}}>⟲</span>:<span style={{color:"#333"}}>—</span>}</td>
                          {visibleFields.map(f=><td key={f.key} style={{ padding:"3px 6px", ...TC_BASE, maxWidth:150 }}>{r[f.key]||"—"}</td>)}
                          <td style={{ padding:"3px 6px", textAlign:"center" }}>{ex?.isHighend?"✅":""}</td>
                        </tr>
                      );
                    })}
                    {syncPreview.rows.length>50 && <tr><td colSpan={2+visibleFields.length} style={{ padding:"3px 6px", color:"#444", fontSize:10 }}>...và {syncPreview.rows.length-50} nữa</td></tr>}
                  </tbody>
                </table>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button className="btn btn-primary" onClick={handleConfirmSync} disabled={syncing}>✅ {t("skm.confirm", { n: syncPreview.products.length })}</button>
                <button className="btn" onClick={()=>{setSyncPreview(null);setSyncMsg("");}}>Hủy</button>
              </div>
            </>
          )}
        </div>
      )}

      {/* ══════════════════ MANUAL FORM ══════════════════ */}
      {showForm && (
        <div className="card" style={{ marginBottom:12 }}>
          <div className="card-header">
            <div className="card-title">Thêm SKU thủ công</div>
            <button className="btn btn-sm" onClick={()=>setShowForm(false)}>✕</button>
          </div>
          <form onSubmit={handleAdd}>
            <div className="grid-3">
              {fields.map(f => (
                <div className="form-group" key={f.key}>
                  <label className="form-label">{f.label}</label>
                  <input value={form[f.key]||""} onChange={e=>setForm(p=>({...p,[f.key]:e.target.value}))} required={f.key==="model"} />
                </div>
              ))}
              <div className="form-group" style={{ gridColumn:"1/-1" }}>
                <label className="form-label">Ghi chú</label>
                <textarea value={form.notes||""} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} rows={2} style={{ resize:"vertical" }} />
              </div>
            </div>
            <div className="form-group" style={{ display:"flex", alignItems:"center", gap:8 }}>
              <input type="checkbox" id="he" checked={!!form.isHighend} onChange={e=>setForm(f=>({...f,isHighend:e.target.checked}))} style={{ width:"auto" }} />
              <label htmlFor="he" className="form-label" style={{ marginBottom:0 }}>Highend</label>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button className="btn btn-primary" type="submit">Thêm</button>
              <button className="btn" type="button" onClick={()=>setShowForm(false)}>Hủy</button>
            </div>
          </form>
        </div>
      )}

      {/* ══════════════════ BULK BAR ══════════════════ */}
      {checkedIds.size>0 && (
        <div style={{ display:"flex", alignItems:"center", gap:6, padding:"7px 12px",
          background:"#0f172a", border:"1px solid #1e3a5f", borderRadius:6, marginBottom:8, flexWrap:"wrap" }}>
          <span style={{ fontSize:12, color:"#7dd3fc", fontWeight:600 }}>Đã chọn {checkedIds.size}</span>
          <button className="btn btn-sm" onClick={()=>handleBulkSetHighend(true)}>✅ Highend</button>
          <button className="btn btn-sm" onClick={()=>handleBulkSetHighend(false)}>📦 Standard</button>
          <button className="btn btn-sm btn-danger" onClick={handleBulkDelete}>🗑 Xóa {checkedIds.size}</button>
          <button className="btn btn-sm" onClick={()=>setCheckedIds(new Set())} style={{ marginLeft:"auto" }}>✕ Bỏ chọn</button>
        </div>
      )}

      {/* ══════════════════ PAGINATION ══════════════════ */}
      <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6, flexWrap:"wrap" }}>
        <div style={{ display:"flex", alignItems:"center", gap:4, fontSize:11, color:"#555" }}>
          {PAGE_SIZES.map(n=>(
            <button key={n} onClick={()=>setPageSize(n)}
              style={{ padding:"2px 7px", fontSize:10, borderRadius:4, border:"1px solid", cursor:"pointer",
                background:pageSize===n?"#3b82f6":"#111", borderColor:pageSize===n?"#3b82f6":"#222",
                color:pageSize===n?"#fff":"#555" }}>{n}</button>
          ))}
          <span style={{ marginLeft:2 }}>/ trang</span>
        </div>
        <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:4, fontSize:11, color:"#444" }}>
          <button className="btn btn-sm" onClick={()=>setPage(p=>Math.max(0,p-1))} disabled={page===0} style={{ padding:"1px 7px" }}>‹</button>
          <span>{totalPages>0?`${page+1}/${totalPages}`:"0/0"}</span>
          <button className="btn btn-sm" onClick={()=>setPage(p=>Math.min(totalPages-1,p+1))} disabled={page>=totalPages-1} style={{ padding:"1px 7px" }}>›</button>
          <span style={{ color:"#333" }}>({filteredSkus.length})</span>
        </div>
      </div>

      {/* ══════════════════ SKU TABLE ══════════════════ */}
      <div style={{ overflowX:"auto", borderRadius:8, border:"1px solid #e5e7eb" }}>
        <table className="sku-table" style={{ borderCollapse:"collapse", tableLayout:"fixed", width:"100%", minWidth:620 }}>
          <colgroup>
            <col style={{ width:32 }} />
            {visibleFields.map(f=><col key={f.key} style={{ width:colWidth(f.key) }} />)}
            <col style={{ width:54 }} />
          </colgroup>
          <thead>
            <tr>
              <th style={{ ...TH_BASE, padding:"8px 8px", textAlign:"center", position:"sticky", left:0, zIndex:4 }}>
                <input type="checkbox" checked={allPageChecked} onChange={togglePage} style={{ width:"auto", cursor:"pointer" }} />
              </th>
              {visibleFields.map(f=><th key={f.key} style={{ ...TH_BASE, padding:"8px 8px", ...(f.key==="model" ? { position:"sticky", left:32, zIndex:4, boxShadow:"2px 0 4px -2px rgba(0,0,0,0.25)" } : {}) }}>{f.label}</th>)}
              <th style={{ ...TH_BASE, padding:"8px 8px", textAlign:"center" }}>Loại</th>
            </tr>
            {/* ── Dynamic filter row ── */}
            <tr style={{ background:"#FFF6F6", borderBottom:"1px solid #F3D6D6" }}>
              <td style={{ position:"sticky", left:0, zIndex:3, background:"#FFF6F6" }}></td>
              {visibleFields.map(f => {
                const ft = filterType(skus, f.key);
                const opts = uniqueVals(skus, f.key);
                return (
                  <td key={f.key} style={f.key==="model" ? { position:"sticky", left:32, zIndex:3, background:"#FFF6F6", boxShadow:"2px 0 4px -2px rgba(0,0,0,0.15)" } : undefined}>
                    {ft === "select"
                      ? <select value={filters[f.key]||""} onChange={e=>setFilter(f.key,e.target.value)} style={FS}>
                          {opts.map(v=><option key={v} value={v}>{v||`Tất cả ${f.label}`}</option>)}
                        </select>
                      : <input value={filters[f.key]||""} onChange={e=>setFilter(f.key,e.target.value)}
                          placeholder={`Lọc ${f.label}…`} style={FI} />
                    }
                  </td>
                );
              })}
              <td>
                <select value={filters.loai||""} onChange={e=>setFilter("loai",e.target.value)} style={FS}>
                  <option value="">Tất cả</option>
                  <option value="highend">Highend</option>
                  <option value="standard">Standard</option>
                </select>
              </td>
            </tr>
          </thead>
          <tbody>
            {pagedSkus.map(s=>(
              <tr key={s.id} className={checkedIds.has(s.id)?"sku-checked":undefined} style={{ borderBottom:"1px solid #f0f0f0" }}>
                <td className="sku-sticky" style={{ padding:"6px 8px", textAlign:"center", position:"sticky", left:0, zIndex:1 }}>
                  <input type="checkbox" checked={checkedIds.has(s.id)} onChange={()=>toggleOne(s.id)} style={{ width:"auto", cursor:"pointer" }} />
                </td>
                {visibleFields.map(f=>(
                  <td key={f.key} className={f.key==="model"?"sku-sticky":undefined}
                    style={{ padding:"6px 8px", cursor:f.key==="model"?"pointer":undefined,
                      ...(f.key==="model" ? { position:"sticky", left:32, zIndex:1, boxShadow:"2px 0 4px -2px rgba(0,0,0,0.15)" } : {}) }}
                    onClick={f.key==="model"?()=>openEdit(s):undefined}>
                    {f.key==="model" ? (
                      <>
                        <div style={{ ...TC_BASE, whiteSpace:"normal", wordBreak:"break-word", overflow:"visible", fontWeight:500, color:isDisc(s)?"#999":"#C8152A", textDecoration:isDisc(s)?"line-through":"none" }}>{s.model}</div>
                        {isDisc(s) && <span className="badge badge-red" style={{ fontSize:9, padding:"0 4px" }}>{t("skm.st_disc")}</span>}
                        {s.notes && <div style={{ fontSize:10, color:"#f59e0b", ...TC_BASE }} title={s.notes}>📝 {s.notes}</div>}
                      </>
                    ) : (
                      <div style={{ ...TC_BASE, color:f.key==="partNo"?"#555":f.key==="series"||f.key==="segment"?"#888":undefined }}
                        title={s[f.key]}>{s[f.key]||"—"}</div>
                    )}
                  </td>
                ))}
                <td style={{ padding:"6px 8px", textAlign:"center" }}>
                  <span className={"badge "+(s.isHighend?"badge-blue":"badge-gray")} style={{ fontSize:10, padding:"1px 5px" }}>
                    {s.isHighend?"High":"Std"}
                  </span>
                </td>
              </tr>
            ))}
            {pagedSkus.length===0 && (
              <tr><td colSpan={2+visibleFields.length} style={{ padding:20, color:"#444", textAlign:"center" }}>
                {skus.length===0?t("skm.empty"):'Không có SKU khớp bộ lọc.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* ══════════════════ DETAIL MODAL ══════════════════ */}
      {selected && editForm && (
        <div className="modal-overlay" onClick={()=>{setSelected(null);setEditForm(null);}}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{ maxWidth:620, maxHeight:"90vh", overflowY:"auto" }}>
            <div className="modal-header">
              <div className="modal-title" style={{ fontSize:14 }}>{selected.model}</div>
              <div style={{ display:"flex", gap:6 }}>
                <button className="btn btn-sm btn-danger"
                  onClick={async()=>{await deleteSKU(selected.id);setSelected(null);setEditForm(null);getAllSKUs().then(setSKUs);}}>Xóa</button>
                <button className="btn btn-sm" onClick={()=>{setSelected(null);setEditForm(null);}}>✕</button>
              </div>
            </div>
            <div style={{ fontSize:11, color:"#888", marginBottom:6 }}>
              {selected.partNo}
              {isDisc(selected) && <span className="badge badge-red" style={{ marginLeft:6, fontSize:10 }}>{t("skm.st_disc")}</span>}
            </div>
            <div style={{ marginBottom:14 }}>
              <SkuSpecSheet sku={selected} fallback={
                <table style={{ fontSize:12, width:"100%" }}><tbody>
                  {fields.map(f=>selected[f.key]?(
                    <tr key={f.key}>
                      <td style={{ color:"#555", width:90, padding:"3px 0", fontSize:11, verticalAlign:"top" }}>{f.label}</td>
                      <td style={{ padding:"3px 0" }}>{selected[f.key]}</td>
                    </tr>
                  ):null)}
                </tbody></table>
              } />
            </div>
            <div style={{ borderTop:"1px solid #1a1a1a", paddingTop:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
                <label style={{ fontSize:12, color:"#888", display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}>
                  <input type="checkbox" checked={editForm.isHighend}
                    onChange={e=>setEditForm(f=>({...f,isHighend:e.target.checked}))} style={{ width:"auto" }} />
                  Highend
                </label>
                <span className={"badge "+(editForm.isHighend?"badge-blue":"badge-gray")} style={{ fontSize:11 }}>
                  {editForm.isHighend?"Highend":"Standard"}
                </span>
              </div>
              <div className="form-group" style={{ marginBottom:10 }}>
                <label className="form-label" style={{ fontSize:11 }}>📝 Ghi chú</label>
                <textarea value={editForm.notes} onChange={e=>setEditForm(f=>({...f,notes:e.target.value}))}
                  rows={3} placeholder="Nhập ghi chú tuỳ ý…" style={{ resize:"vertical", fontSize:12 }} />
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <button className="btn btn-primary btn-sm" onClick={saveEdit}>💾 Lưu</button>
                <button className="btn btn-sm" onClick={()=>{setSelected(null);setEditForm(null);}}>Hủy</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
